import {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

export interface Theme {
  colors: {
    text: string;
    background: string;
    header: {
      background: string;
      text: string;
    };
    footer: {
      background: string;
      text: string;
    };
    gpt: {
      background: string;
    };
  };
}

export const defaultTheme: Theme = {
  colors: {
    text: "#071435",
    background: "#ffffff",
    header: {
      background: "#ffffff",
      text: "#071435",
    },
    footer: {
      background: "#ffffff",
      text: "#071435",
    },
    gpt: {
      background:
        "linear-gradient(rgba(255, 208, 0, 0.2) 0%, rgba(255, 161, 0, 0.2) 100%), rgb(255, 255, 255)",
    },
  },
};

interface ThemeContextValue {
  theme: Theme;
  setTheme: (theme: Theme | ((prevTheme: Theme) => Theme)) => void;
  resetTheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

/**
 * Hook to access and update the theme from child components.
 */
export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error("useTheme must be used within ThemeProvider");
  }
  return context;
}

export function ThemeProvider({
  initialTheme,
  children,
}: {
  initialTheme?: Theme;
  children: ReactNode;
}) {
  const [theme, setTheme] = useState<Theme>(initialTheme ?? defaultTheme);

  useEffect(() => {
    if (initialTheme) {
      setTheme(initialTheme);
    }
  }, [initialTheme]);

  const resetTheme = () => {
    setTheme(defaultTheme);
  };

  return (
    <ThemeContext.Provider
      value={{
        theme,
        setTheme,
        resetTheme,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
}

export function RoomThemeProvider({
  backgroundColor,
  textColor,
  children,
}: {
  backgroundColor: string | undefined;
  textColor: string | undefined;
  children: ReactNode;
}) {
  const theme = useMemo(
    (): Theme => ({
      ...defaultTheme,
      colors: {
        ...defaultTheme.colors,
        text: textColor ?? defaultTheme.colors.text,
        background: backgroundColor ?? defaultTheme.colors.background,
        header: {
          background: "#ffffff",
          text: "#071435",
        },
        footer: {
          background: "#212121",
          text: "#ffffff",
        },
      },
    }),
    [backgroundColor, textColor]
  );

  return <ThemeProvider initialTheme={theme}>{children}</ThemeProvider>;
}

export const DefaultThemeProvider = ThemeProvider;
